import React, { useState } from "react";
import { Package, Plus } from "lucide-react";
import { toast } from "sonner";
import { TagPreview } from "../../lib/tagShapes";

export const BundlePanel = ({ bundles, shapes, colours, onAdd }) => {
  const [openId, setOpenId] = useState(null);
  const [shapeId, setShapeId] = useState(shapes[0]?.id || "");
  const [colourId, setColourId] = useState(colours[0]?.id || "");
  const [names, setNames] = useState("");

  if (!bundles?.length) return null;
  const shape = shapes.find((s) => s.id === shapeId);
  const colour = colours.find((c) => c.id === colourId);

  const addBundle = (bundle) => {
    const texts = names.split("\n").map((t) => t.trim()).filter(Boolean);
    if (!texts.length) { toast.error("Enter at least one plant name (one per line)"); return; }
    if (texts.length > bundle.tag_count) { toast.error(`This bundle includes up to ${bundle.tag_count} tags`); return; }
    if (!shape || !colour) return;
    onAdd({
      bundle_id: bundle.id, bundle_name: bundle.name,
      shape_id: shape.id, shape_key: shape.key, shape_name: shape.name,
      colour_id: colour.id, colour_name: colour.name, colour_hex: colour.hex,
      texts, text_line1: texts[0], text_line2: "", size: "",
      quantity: 1, unit_price: Number(bundle.price),
      uid: crypto.randomUUID(),
    });
    setNames("");
    setOpenId(null);
    toast.success(`${bundle.name} added to your order`);
  };

  return (
    <div data-testid="bundle-deals">
      <h3 className="font-serif-display uppercase text-plum-deep text-xl md:text-2xl tracking-[0.08em] flex items-center gap-3">
        <span className="w-9 h-9 rounded-full bg-forest text-cream flex items-center justify-center shrink-0"><Package className="w-4 h-4" /></span>
        BUNDLE DEALS
      </h3>
      <div className="mt-5 space-y-4">
        {bundles.map((b) => (
          <div key={b.id} className="bg-warmwhite border-2 border-forest/25 rounded-3xl p-5" data-testid={`bundle-card-${b.id}`}>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-[180px]">
                <p className="font-serif-display uppercase tracking-[0.1em] text-plum-deep">{b.name}</p>
                <p className="text-xs text-plum-deep/60 mt-0.5">{b.description || `${b.tag_count} custom tags at one bundle price`}</p>
              </div>
              <span className="font-serif-display text-forest text-xl font-semibold">${Number(b.price).toFixed(2)}</span>
              <span className="text-[11px] uppercase tracking-[0.15em] bg-forest/10 text-forest rounded-full px-3 py-1 font-semibold">{b.tag_count} TAGS</span>
              <button
                onClick={() => setOpenId(openId === b.id ? null : b.id)}
                data-testid={`bundle-build-${b.id}`}
                className="rounded-full bg-forest text-cream px-5 py-2.5 text-xs uppercase tracking-[0.14em] font-semibold hover:bg-plum transition-colors"
              >
                {openId === b.id ? "CLOSE" : "BUILD THIS BUNDLE"}
              </button>
            </div>
            {openId === b.id && (
              <div className="mt-5 grid md:grid-cols-[1fr_auto] gap-5 items-start" data-testid="bundle-builder">
                <div className="space-y-4">
                  <div className="flex flex-wrap gap-3 items-center">
                    <select
                      value={shapeId} onChange={(e) => setShapeId(e.target.value)}
                      data-testid="bundle-shape-select"
                      className="rounded-full px-4 py-2.5 bg-cream border border-plum-deep/15 text-sm focus:outline-none"
                    >
                      {shapes.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                    <div className="flex gap-2">
                      {colours.map((c) => (
                        <button
                          key={c.id}
                          onClick={() => setColourId(c.id)}
                          data-testid={`bundle-colour-${c.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                          title={c.name}
                          className={`w-8 h-8 rounded-full border-2 transition-transform hover:scale-110 ${colourId === c.id ? "border-plum-deep scale-110" : "border-plum-deep/15"}`}
                          style={{ background: c.hex }}
                        />
                      ))}
                    </div>
                  </div>
                  <textarea
                    value={names}
                    onChange={(e) => setNames(e.target.value)}
                    rows={4}
                    placeholder={`One plant name per line (up to ${b.tag_count})\nCAFE AU LAIT\nBLACK JACK\n…`}
                    data-testid="bundle-names-input"
                    className="w-full rounded-3xl px-5 py-3 bg-cream border border-plum-deep/15 text-sm focus:outline-none focus:border-forest resize-none"
                  />
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => addBundle(b)}
                      data-testid="bundle-add-btn"
                      className="inline-flex items-center gap-2 rounded-full bg-forest text-cream px-6 py-3 text-xs uppercase tracking-[0.16em] font-semibold hover:bg-plum transition-colors"
                    >
                      <Plus className="w-3.5 h-3.5" /> ADD BUNDLE TO ORDER
                    </button>
                    <span className="text-xs text-plum-deep/60">{names.split("\n").filter((t) => t.trim()).length}/{b.tag_count} names</span>
                  </div>
                </div>
                <TagPreview
                  shapeKey={shape?.key}
                  hex={colour?.hex || "#EC2675"}
                  line1={names.split("\n").find((t) => t.trim()) || "YOUR TEXT"}
                  className="w-24 mx-auto hidden md:block"
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
